/**
 * Created by Ella Ma on 09/23/19.
 * Description:
 * implement interface of dashboard tiles for tam dashboard pie chart tile
 * ------ maintenance history ------
 */

import { DashboardTileAction, TamDashboardChartTileBaseConfiguration } from '../tam-dashboard-tile.base.config';
import { Subject, Subscription } from 'rxjs';
import { TamDashboardActions, TamDashboardEvents, PieChartDisplaySetting, TamDashboardTileType } from '../tam-dashboard.model';
import { DashboardService } from '../../../pages/dashboard/dashboard.service';
import { filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../../../redux';
import { DashboardActionTypes } from '../../../redux/actions/dashboard.actions';
import { DashboardTileService } from '../dashboard-tile.service';
import { formatNumber } from '@telerik/kendo-intl';

export class TamDashboardTileData {
    constructor(public category: string, public value: any, public valueText: string) { }
}

export class TamDashboardPieChartTileConfiguration implements TamDashboardChartTileBaseConfiguration {

    public data: TamDashboardTileData[];
    public id: string;
    public initialized: boolean;
    public name: string;
    public readonly: boolean;
    public seriesClickEventType: string;

    public actionSubject$: Subject<any>;
    public feedbackSubject$: Subject<any>;
    public subscriptions: Subscription[] = [];

    private _displaySetting: string = PieChartDisplaySetting.PERCENTAGE;
    private _focusedEntityIds;
    private _labelSettings: any;
    private _tileConfig: any;

    constructor(
        private _dashboardTileService?: DashboardTileService,
        private _dashboardService?: DashboardService,
        private _store?: Store<AppState>,
    ) {
        this.initialized = false;
    }

    destroy() {
        this.subscriptions.forEach((subscription: Subscription) => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
    }

    init(id: string, readonly: boolean, name: string, actionSubject$: Subject<any>, feedbackSubject$: Subject<any>): boolean {
        if (this.initialized === false) {
            this.id = id;
            this.readonly = readonly;
            this.name = name;
            this.actionSubject$ = actionSubject$;
            this.feedbackSubject$ = feedbackSubject$;
            this.subscriptions.push(
                this.actionSubject$.pipe(
                    filter((action: any) => action && action.id === this.id)
                ).subscribe((action) => this._actionHandler(action))
            );
            this.initialized = true;
            return true;
        } else {
            return false;
        }
    }

    legendLabedContent(e) {
        const str = e.text;
        if (str.length > 20) {
            return str.slice(0, 20) + '...';
        }
        return str;
    }

    labelContent = (e: any) => {
        if (this._displaySetting === PieChartDisplaySetting.VALUE || e.format) {
            if (this._labelSettings) {
                const value = e.value;
                return this._dashboardService.cellRenderer(this._labelSettings, { value });
            }
            return e.value;
        }
        return formatNumber(e.percentage, 'p2');
    };

    onSeriesClick = (event: any) => {
        if (!this.readonly) {
            return;
        }
        const categoryAxis = this._tileConfig.visualization.settings.categoryAxis;
        const category = this._dashboardTileService.formatSeriesClickCategoryValue(this._tileConfig.columns, categoryAxis, event.category);
        this.feedbackSubject$.next({
            type: this.seriesClickEventType || TamDashboardEvents.OPEN_DRILL_DOWN,
            payload: {
                id: this.id,
                value: event.value,
                category: category,
                type: TamDashboardTileType.PIE_CHART.toString(),
                name: this.name,
                readonly: this.readonly,
                tileConfig: this._tileConfig,
                focusedEntityIds: this._focusedEntityIds,
                actionSubject$: this.actionSubject$,
                feedbackSubject$: this.feedbackSubject$,
                data: this.data
            }
        });
    };

    setTileData(data) {
        this.actionSubject$.next({ id: this.id, type: TamDashboardActions.SET_TILE_DATA, payload: data });
    }

    private _actionHandler(action: DashboardTileAction) {
        switch (action.type) {
            case TamDashboardActions.LOAD_DATA: {
                if (action.payload && action.payload['tileConfig']) {
                    this._loadData(action.payload);
                }
                break;
            }
            case TamDashboardActions.SET_CHART_LABEL_SETTING: {
                this._labelSettings = action.payload;
                break;
            }
            case TamDashboardActions.SET_TILE_CONFIG: {
                this._tileConfig = action.payload;
                break;
            }
            case TamDashboardActions.SET_FOCUSED_ENTITY_IDS: {
                this._focusedEntityIds = action.payload;
                break;
            }
            case TamDashboardActions.SET_PIR_CHART_DISPLAY: {
                this._displaySetting = action.payload || PieChartDisplaySetting.PERCENTAGE;
            }
        }
    }

    private _loadData({ tileConfig, focusedEntityIds, useConfig, saveStore = false }) {
        this._dashboardTileService.pieChartLoadData(tileConfig, focusedEntityIds, useConfig).subscribe((data) => {
            this.data = data;
            if (saveStore && data && this._store) {
                this._store.dispatch({
                    type: DashboardActionTypes.SAVE_TILE_DATA,
                    payload: {
                        id: this.id,
                        data: data,
                    }
                });
            }
            this.setTileData(data);
        });
    }
}
